import { AiSystemDeployment } from '@/app/interfaces/aiSystemDeployment';
import {
  DeploySystemCorrectionDetail,
  DeploySystemCorrectionFieldMapEntry,
  DeploySystemCorrectionFormField,
  DeploySystemCorrectionObservedField,
  DeploySystemCorrectionRole,
  DeploySystemCorrectionStep,
  DeploySystemCorrectionStepSummary,
  DeploySystemCorrectionSubmitPayload,
} from '@/app/interfaces/deploy-system-correction';

type BinaryFormValue = 'Sí' | 'No';

type BinarySourceValue = boolean | string | null | undefined;

const toNullableString = (value: string | null | undefined): string | null => {
  if (value == null || value === '') {
    return null;
  }

  return value;
};

const toBinaryFormValue = (value: BinarySourceValue): BinaryFormValue | null => {
  if (value === true) return 'Sí';
  if (value === false) return 'No';

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();

    if (normalizedValue === 'true' || normalizedValue === 'si' || normalizedValue === 'sí') {
      return 'Sí';
    }

    if (normalizedValue === 'false' || normalizedValue === 'no') {
      return 'No';
    }
  }

  return null;
};

const toBackendBooleanValue = (value: string | null): boolean | null => {
  if (value === 'Sí' || value === 'Si' || value === 'si­') return true;
  if (value === 'No') return false;
  return null;
};

const toNullableStringArray = (value: string[] | null | undefined): string[] | null => value ?? null;

const stepLabels: Record<DeploySystemCorrectionStep, string> = {
  1: 'Gestión del riesgo',
  2: 'Cumplimiento RIA',
  3: 'Cuestiones técnicas',
  4: 'ISO 42001 y HAP',
  5: 'Cuestiones técnicas',
  6: 'Datos económicos',
};

const fieldMapEntry = (
  detailField: string,
  formField: DeploySystemCorrectionFormField,
  step: DeploySystemCorrectionStep,
  fieldLabel: string
): DeploySystemCorrectionFieldMapEntry => ({
  detailField,
  formField,
  step,
  stepLabel: stepLabels[step],
  fieldLabel,
});

export const deploySystemCorrectionFieldMap: readonly DeploySystemCorrectionFieldMapEntry[] = [
  fieldMapEntry('systemName', 'systemName', 1, 'Nombre del sistema'),
  fieldMapEntry('systemCode', 'systemCode', 1, 'Código del sistema'),
  fieldMapEntry('systemClassification', 'systemClassification', 1, 'Clasificación del sistema'),

  fieldMapEntry('biases', 'biases', 1, 'Sesgos identificados'),
  fieldMapEntry('risks', 'risks', 1, 'Riesgos identificados'),

  fieldMapEntry('usageInstructions', 'usageInstructions', 2, 'Instrucciones de uso'),
  fieldMapEntry('humanSupervision', 'humanSupervision', 2, 'Supervisión humana'),
  fieldMapEntry('inputDataQuality', 'inputDataQuality', 2, 'Calidad de datos de entrada'),
  fieldMapEntry('qualityMonitoring', 'qualityMonitoring', 2, 'Monitorización continua de la calidad de los datos'),
  fieldMapEntry('logRetention', 'logRetention', 2, 'Conservación de registros'),
  fieldMapEntry('fria', 'fria', 2, 'Evaluación de impacto sobre derechos fundamentales'),
  fieldMapEntry('dpia', 'dpia', 2, 'EIPD'),
  fieldMapEntry(
    'naturalPersonInteractionCheck',
    'naturalPersonInteraction',
    2,
    'Interacción directa con personas físicas'
  ),
  fieldMapEntry('contentGenerationCheck', 'contentGeneration', 2, 'Generación de contenidos sintéticos'),
  fieldMapEntry('biometricsEmotionsCheck', 'biometricsEmotions', 2, 'Reconocimiento de emociones o biometría'),
  fieldMapEntry('publicInterestTextCheck', 'publicInterestText', 2, 'Generación o manipulación de texto'),
  fieldMapEntry('userInformation', 'userInformation', 2, 'Información al usuario'),

  fieldMapEntry('infrastructure', 'aiInfrastructure', 3, 'Infraestructura'),
  fieldMapEntry('aiInfrastructure', 'aiInfrastructure', 3, 'Infraestructura'),
  fieldMapEntry('integrationMode', 'integrationMode', 3, 'Modo de integración'),
  fieldMapEntry('dataSource', 'dataSource', 3, 'Origen de los datos'),
  fieldMapEntry('timePeriod', 'timePeriod', 3, 'Periodo temporal cubierto'),
  fieldMapEntry('dataCategory', 'dataCategory', 3, 'Tipo de datos'),
  fieldMapEntry('qualityAccreditation', 'qualityAccreditation', 3, 'Acreditación de la calidad de los datos'),
  fieldMapEntry('inputDataSource', 'inputDataSource', 3, 'Origen de la fuente de datos en tiempo real'),
  fieldMapEntry('sourceCode', 'sourceCode', 3, 'Enlace al código fuente'),

  fieldMapEntry('iso42001Check', 'iso42001', 4, 'Validación contra controles ISO 42001'),
  fieldMapEntry('csvISO42001', 'iso42001Csv', 4, 'CSV ISO 42001'),
  fieldMapEntry('iso42001', 'iso42001Description', 4, 'Descripción ISO 42001'),

  fieldMapEntry('dataSovereigntyCheck', 'dataSovereignty', 4, 'Soberanía del dato protegida'),
  fieldMapEntry('dataSovereignty', 'dataSovereigntyDescription', 4, 'Descripción de soberanía del dato'),
  fieldMapEntry('equityAndAccessCheck', 'equityAndAccess', 4, 'Equidad y acceso universal verificado'),
  fieldMapEntry('equityAndAccess', 'equityAndAccessDescription', 4, 'Descripción de equidad y acceso universal'),

  fieldMapEntry('updateCheck', 'aiSystemUpdate', 5, 'Frecuencia y política de actualización'),
  fieldMapEntry('aiSystemUpdate', 'aiSystemUpdateDescription', 5, 'Descripción de política de actualización'),
  fieldMapEntry('latencyCheck', 'latencyPerformance', 5, 'Latencia y rendimiento verificados'),
  fieldMapEntry('latencyPerformance', 'latencyPerformanceDescription', 5, 'Descripción de latencia y rendimiento'),
  fieldMapEntry('scalabilityCheck', 'scalability', 5, 'Escalabilidad documentada'),
  fieldMapEntry('csvScalability', 'scalabilityCsv', 5, 'CSV escalabilidad'),
  fieldMapEntry('contingencyPlanCheck', 'contingencyPlan', 5, 'Plan de contingencia y desactivación'),
  fieldMapEntry('csvContingencyPlan', 'contingencyPlanCsv', 5, 'CSV plan de contingencia'),
  fieldMapEntry('cybersecurityCheck', 'cybersecurity', 5, 'Ciberseguridad integrada'),
  fieldMapEntry('csvCybersecurity', 'cybersecurityCsv', 5, 'CSV ciberseguridad'),
  fieldMapEntry('cybersecurity', 'cybersecurityDescription', 5, 'Descripción de ciberseguridad'),
  fieldMapEntry('accessControlCheck', 'accessControl', 5, 'Control de acceso y autenticación'),
  fieldMapEntry('accessControl', 'accessControlDescription', 5, 'Descripción de control de acceso'),
  fieldMapEntry('attackProtectionCheck', 'attackProtection', 5, 'Protección contra ataques adversariales'),
  fieldMapEntry('csvAttackProtection', 'attackProtectionCsv', 5, 'CSV protección contra ataques'),
  fieldMapEntry('attackProtection', 'attackProtectionDescription', 5, 'Descripción de protección contra ataques'),
  fieldMapEntry('resilienceCheck', 'resilience', 5, 'Resiliencia frente a fallos'),
  fieldMapEntry('csvResilience', 'resilienceCsv', 5, 'CSV resiliencia'),
  fieldMapEntry('resilience', 'resilienceDescription', 5, 'Descripción de resiliencia'),

  fieldMapEntry('costBenefitAnalysisCheck', 'costBenefitAnalysis', 6, 'Análisis coste-beneficio'),
  fieldMapEntry('csvCostBenefit', 'costBenefitAnalysisCsv', 6, 'CSV análisis coste-beneficio'),
  fieldMapEntry('costBenefitAnalysis', 'costBenefitAnalysisDescription', 6, 'Descripción de análisis coste-beneficio'),
  fieldMapEntry('publicValue', 'publicValue', 6, 'Medición de valor público'),
  fieldMapEntry('publicValueCheck', 'publicValue', 6, 'Medición de valor público'),
  fieldMapEntry('publicValueDescription', 'publicValueDescription', 6, 'Descripción de valor público'),
];

export const deploySystemDetailFieldToFormFieldMap: Readonly<Record<string, DeploySystemCorrectionFormField>> =
  deploySystemCorrectionFieldMap.reduce<Record<string, DeploySystemCorrectionFormField>>((acc, entry) => {
    acc[entry.detailField] = entry.formField;
    return acc;
  }, {});

const deploySystemDetailFieldToMapEntry: Readonly<Record<string, DeploySystemCorrectionFieldMapEntry>> =
  deploySystemCorrectionFieldMap.reduce<Record<string, DeploySystemCorrectionFieldMapEntry>>((acc, entry) => {
    acc[entry.detailField] = entry;
    return acc;
  }, {});

export function mapDeploySystemCorrectionDetailToForm(detail: DeploySystemCorrectionDetail): AiSystemDeployment {
  return {
    systemName: toNullableString(detail.systemName),
    systemCode: toNullableString(detail.systemCode),
    systemClassification: toNullableString(detail.systemClassification),
    biases: toNullableString(detail.biases),
    risks: toNullableString(detail.risks),
    usageInstructions: toNullableString(detail.usageInstructions),
    humanSupervision: toNullableString(detail.humanSupervision),
    inputDataQuality: toNullableString(detail.inputDataQuality),
    qualityMonitoring: toNullableString(detail.qualityMonitoring),
    logRetention: toNullableString(detail.logRetention),
    fria: toNullableString(detail.fria),
    dpia: toNullableString(detail.dpia),
    naturalPersonInteraction: toBinaryFormValue(detail.naturalPersonInteractionCheck),
    contentGeneration: toBinaryFormValue(detail.contentGenerationCheck),
    biometricsEmotions: toBinaryFormValue(detail.biometricsEmotionsCheck),
    publicInterestText: toBinaryFormValue(detail.publicInterestTextCheck),
    userInformation: toNullableString(detail.userInformation),
    aiInfrastructure: toNullableString(detail.infrastructure),
    integrationMode: toNullableString(detail.integrationMode),
    dataSource: toNullableString(detail.dataSource),
    timePeriod: toNullableString(detail.timePeriod),
    dataCategory: toNullableStringArray(detail.dataCategory),
    qualityAccreditation: toNullableString(detail.qualityAccreditation),
    inputDataSource: toNullableString(detail.inputDataSource),
    sourceCode: toNullableString(detail.sourceCode),
    iso42001: toBinaryFormValue(detail.iso42001Check),
    iso42001Csv: toNullableString(detail.csvISO42001),
    iso42001Description: toNullableString(detail.iso42001),
    dataSovereignty: toBinaryFormValue(detail.dataSovereigntyCheck),
    dataSovereigntyDescription: toNullableString(detail.dataSovereignty),
    equityAndAccess: toBinaryFormValue(detail.equityAndAccessCheck),
    equityAndAccessDescription: toNullableString(detail.equityAndAccess),
    aiSystemUpdate: toBinaryFormValue(detail.updateCheck),
    aiSystemUpdateDescription: toNullableString(detail.aiSystemUpdate),
    latencyPerformance: toBinaryFormValue(detail.latencyCheck),
    latencyPerformanceDescription: toNullableString(detail.latencyPerformance),
    scalability: toBinaryFormValue(detail.scalabilityCheck),
    scalabilityCsv: toNullableString(detail.csvScalability),
    contingencyPlan: toBinaryFormValue(detail.contingencyPlanCheck),
    contingencyPlanCsv: toNullableString(detail.csvContingencyPlan),
    cybersecurity: toBinaryFormValue(detail.cybersecurityCheck),
    cybersecurityCsv: toNullableString(detail.csvCybersecurity),
    cybersecurityDescription: toNullableString(detail.cybersecurity),
    accessControl: toBinaryFormValue(detail.accessControlCheck),
    accessControlDescription: toNullableString(detail.accessControl),
    attackProtection: toBinaryFormValue(detail.attackProtectionCheck),
    attackProtectionCsv: toNullableString(detail.csvAttackProtection),
    attackProtectionDescription: toNullableString(detail.attackProtection),
    resilience: toBinaryFormValue(detail.resilienceCheck),
    resilienceCsv: toNullableString(detail.csvResilience),
    resilienceDescription: toNullableString(detail.resilience),
    costBenefitAnalysis: toBinaryFormValue(detail.costBenefitAnalysisCheck),
    costBenefitAnalysisCsv: toNullableString(detail.csvCostBenefit),
    costBenefitAnalysisDescription: toNullableString(detail.costBenefitAnalysis),
    publicValue: toBinaryFormValue(detail.publicValue ?? detail.publicValueCheck),
    publicValueDescription: toNullableString(detail.publicValueDescription),
  };
}

export function mapDeploySystemCorrectionFormToPayload(
  deployment: AiSystemDeployment,
  executionSystemId: number,
  role?: DeploySystemCorrectionRole | null,
  orgCode?: string | null
): DeploySystemCorrectionSubmitPayload {
  return {
    executionSystemId,
    role,
    orgCode,
    biases: toNullableString(deployment.biases),
    risks: toNullableString(deployment.risks),
    usageInstructions: toNullableString(deployment.usageInstructions),
    csvHumanSupervision: toNullableString(deployment.humanSupervision),
    inputDataQuality: toNullableString(deployment.inputDataQuality),
    qualityMonitoring: toNullableString(deployment.qualityMonitoring),
    logRetention: toNullableString(deployment.logRetention),
    fria: toNullableString(deployment.fria),
    dpia: toNullableString(deployment.dpia),
    naturalPersonInteractionCheck: toBackendBooleanValue(deployment.naturalPersonInteraction),
    contentGenerationCheck: toBackendBooleanValue(deployment.contentGeneration),
    biometricsEmotionsCheck: toBackendBooleanValue(deployment.biometricsEmotions),
    publicInterestTextCheck: toBackendBooleanValue(deployment.publicInterestText),
    userInformation: toNullableString(deployment.userInformation),
    aiInfrastructure: toNullableString(deployment.aiInfrastructure),
    integrationMode: toNullableString(deployment.integrationMode),
    dataSource: toNullableString(deployment.dataSource),
    timePeriod: toNullableString(deployment.timePeriod),
    dataCategory: deployment.dataCategory ?? [],
    csvQualityAccreditation: toNullableString(deployment.qualityAccreditation),
    inputDataSource: toNullableString(deployment.inputDataSource),
    sourceCode: toNullableString(deployment.sourceCode),
    iso42001Check: toBackendBooleanValue(deployment.iso42001),
    iso42001: toNullableString(deployment.iso42001Description),
    csvISO42001: toNullableString(deployment.iso42001Csv),
    dataSovereigntyCheck: toBackendBooleanValue(deployment.dataSovereignty),
    dataSovereignty: toNullableString(deployment.dataSovereigntyDescription),
    equityAndAccessCheck: toBackendBooleanValue(deployment.equityAndAccess),
    equityAndAccess: toNullableString(deployment.equityAndAccessDescription),
    updateCheck: toBackendBooleanValue(deployment.aiSystemUpdate),
    aiSystemUpdate: toNullableString(deployment.aiSystemUpdateDescription),
    latencyCheck: toBackendBooleanValue(deployment.latencyPerformance),
    latencyPerformance: toNullableString(deployment.latencyPerformanceDescription),
    scalabilityCheck: toBackendBooleanValue(deployment.scalability),
    csvScalability: toNullableString(deployment.scalabilityCsv),
    contingencyPlanCheck: toBackendBooleanValue(deployment.contingencyPlan),
    csvContingencyPlan: toNullableString(deployment.contingencyPlanCsv),
    cybersecurityCheck: toBackendBooleanValue(deployment.cybersecurity),
    cybersecurity: toNullableString(deployment.cybersecurityDescription),
    csvCybersecurity: toNullableString(deployment.cybersecurityCsv),
    accessControlCheck: toBackendBooleanValue(deployment.accessControl),
    accessControl: toNullableString(deployment.accessControlDescription),
    attackProtectionCheck: toBackendBooleanValue(deployment.attackProtection),
    attackProtection: toNullableString(deployment.attackProtectionDescription),
    csvAttackProtection: toNullableString(deployment.attackProtectionCsv),
    resilienceCheck: toBackendBooleanValue(deployment.resilience),
    resilience: toNullableString(deployment.resilienceDescription),
    costBenefitAnalysisCheck: toBackendBooleanValue(deployment.costBenefitAnalysis),
    costBenefitAnalysis: toNullableString(deployment.costBenefitAnalysisDescription),
    csvCostBenefit: toNullableString(deployment.costBenefitAnalysisCsv),
    publicValueCheck: toBackendBooleanValue(deployment.publicValue),
    publicValueDescription: toNullableString(deployment.publicValueDescription),
  };
}

export function mapDeploySystemCorrectionFieldToFormField(detailField: string): DeploySystemCorrectionFormField | null {
  return deploySystemDetailFieldToFormFieldMap[detailField] ?? null;
}

export function mapDeploySystemCorrectionFormFieldToStep(
  formField: DeploySystemCorrectionObservedField
): DeploySystemCorrectionStep | null {
  return deploySystemCorrectionFieldMap.find((entry) => entry.formField === formField)?.step ?? null;
}

export function mapDeploySystemCorrectionFieldsToFormFields(
  detailFields: readonly string[]
): DeploySystemCorrectionFormField[] {
  const mappedFields = detailFields
    .map((detailField) => mapDeploySystemCorrectionFieldToFormField(detailField))
    .filter((formField): formField is DeploySystemCorrectionFormField => formField !== null);

  return [...new Set(mappedFields)];
}

export function mapDeploySystemCorrectionFieldsToSummary(
  detailFields: readonly string[]
): DeploySystemCorrectionStepSummary[] {
  const mappedEntries = detailFields
    .map((detailField) => deploySystemDetailFieldToMapEntry[detailField] ?? null)
    .filter((entry): entry is DeploySystemCorrectionFieldMapEntry => entry !== null);

  const uniqueEntries = [...new Map(mappedEntries.map((entry) => [entry.formField, entry])).values()];

  return uniqueEntries
    .sort((firstEntry, secondEntry) => firstEntry.step - secondEntry.step)
    .reduce<DeploySystemCorrectionStepSummary[]>((summary, entry) => {
      const stepSummary = summary.find((group) => group.step === entry.step);
      const field = {
        formField: entry.formField,
        label: entry.fieldLabel,
      };

      if (stepSummary) {
        stepSummary.fields.push(field);
        return summary;
      }

      summary.push({
        step: entry.step,
        stepLabel: entry.stepLabel,
        fields: [field],
      });

      return summary;
    }, []);
}
